import { z } from 'zod'

import type { ReviewDocumentType } from '@/client'
import type { ReviewListParams } from '@/hooks/queries/review'
import { idParam, pageParam } from '@/lib/listSearch'

/**
 * The view state the review page reads from the URL.
 *
 * The kind of document, the warehouse, the page and the open document (`open`,
 * an id of the kind shown), so a document waiting for review is a link. Every
 * field optional with `.catch(undefined)`; the default kind is applied at read
 * time and stripped on the way out.
 */
export const REVIEW_TYPES = [
  'VO',
  'VI',
  'INVOICE',
  'CDN',
] as const satisfies readonly ReviewDocumentType[]

export const reviewSearchSchema = z.object({
  type: z
    .enum(REVIEW_TYPES)
    .optional()
    .catch(undefined)
    .describe('The kind of document to review: VO, VI, INVOICE or CDN.'),
  warehouse: idParam.describe(
    'The warehouse whose documents are listed; nothing loads without it.',
  ),
  page: pageParam,
  open: idParam.describe('The id of the document open for review, of the kind in `type`.'),
})

export type ReviewSearch = z.infer<typeof reviewSearchSchema>

export const REVIEW_SEARCH_DEFAULTS = {
  // A sales invoice first, as VentaHub opened: it is the one a dispatch waits on.
  type: 'INVOICE',
  page: 1,
  limit: 20,
} as const satisfies { type: ReviewDocumentType; page: number; limit: number }

/** Drop any value that equals its default, so a URL carries only what differs. */
export function withoutSearchDefaults(search: ReviewSearch): ReviewSearch {
  const pruned: ReviewSearch = {}
  if (search.type !== undefined && search.type !== REVIEW_SEARCH_DEFAULTS.type) {
    pruned.type = search.type
  }
  if (search.warehouse !== undefined) pruned.warehouse = search.warehouse
  if (search.page !== undefined && search.page !== REVIEW_SEARCH_DEFAULTS.page) {
    pruned.page = search.page
  }
  if (search.open !== undefined) pruned.open = search.open
  return pruned
}

/** The kind the page shows. */
export function reviewType(search: ReviewSearch): ReviewDocumentType {
  return search.type ?? REVIEW_SEARCH_DEFAULTS.type
}

/** What the list endpoint is asked for; nothing until a warehouse is chosen. */
export function toListParams(search: ReviewSearch): ReviewListParams | undefined {
  if (search.warehouse === undefined) return undefined
  return {
    entityType: reviewType(search),
    warehouseId: search.warehouse,
    page: search.page ?? REVIEW_SEARCH_DEFAULTS.page,
    limit: REVIEW_SEARCH_DEFAULTS.limit,
  }
}
